import { ConfluenceClient } from '@canvas/atlassian-client/confluence';
import { type CreateContentMutationInput } from '@canvas/atlassian-client/confluence/types';

import {
  type AddCommentOpts,
  type UpdateCommentOpts,
  type ListCommentOpts,
  type DeleteCommentOpts,
  type ResolveCommentOpts,
  type CommentReactionOpts,
  type ListCommentReactionOpts,
  type BoardTitleOpts,
  type CommentCountOpts,
  type MentionNotificationOpts,
  type ContentPermissionsOpts,
  type ViewedCommentsOpts,
  type MarkCommentAsViewedOpts,
  type LabelSearchOpts,
  type CanvasSessionBulkTokensOpts,
} from './confluence-service-types';

export class ConfluenceService {
  public constructor(
    private client = new ConfluenceClient(),
    private readonly origin = new URL(globalThis.location.origin)
  ) {}

  private getBaseUrl() {
    const base = this.origin.toString();
    if (base.endsWith('/')) {
      return base.slice(0, -1);
    }

    return base;
  }

  public addComment({
    boardId,
    content,
    parentCommentId,
    createdFrom,
    originalSelection = '',
  }: AddCommentOpts) {
    return this.client.addComment({
      baseUrl: this.getBaseUrl(),
      containerId: boardId,
      content,
      parentCommentId,
      createdFrom,
      originalSelection,
    });
  }

  public updateComment({ commentId, content }: UpdateCommentOpts) {
    return this.client.updateComment({
      baseUrl: this.getBaseUrl(),
      content,
      commentId,
    });
  }

  public deleteComment({ commentId }: DeleteCommentOpts) {
    return this.client.deleteComment({
      baseUrl: this.getBaseUrl(),
      commentId,
    });
  }

  public resolveComment({ commentId, resolved }: ResolveCommentOpts) {
    return this.client.resolveComment({
      baseUrl: this.getBaseUrl(),
      commentId,
      resolved,
    });
  }

  public getCommentDetails({
    contentId,
    commentId,
  }: {
    contentId: string;
    commentId: string;
  }) {
    return this.client.getCommentDetails(this.getBaseUrl(), {
      containerId: contentId,
      commentId,
    });
  }

  public listComments({
    boardId,
    parentCommentId: commentId,
    noCache = false,
    before,
  }: ListCommentOpts) {
    return this.client.listComments({
      baseUrl: this.getBaseUrl(),
      containerId: boardId,
      parentCommentId: commentId,
      noCache: noCache,
      before,
    });
  }

  public getCommentCount({ boardId, noCache = false }: CommentCountOpts) {
    return this.client.getCommentCount({
      baseUrl: this.getBaseUrl(),
      containerId: boardId,
      noCache: noCache,
    });
  }

  public confluence_addCommentReaction({
    containerId,
    commentId,
    emojiId,
    containerType,
    cloudId,
  }: CommentReactionOpts & { cloudId: string }) {
    return this.client.confluence_addCommentReaction({
      baseUrl: this.getBaseUrl(),
      containerId,
      commentId,
      emojiId,
      containerType,
      cloudId,
    });
  }

  public deleteCommentReaction({
    containerId,
    commentId,
    emojiId,
    containerType,
  }: CommentReactionOpts) {
    return this.client.deleteCommentReaction({
      baseUrl: this.getBaseUrl(),
      containerId,
      commentId,
      emojiId,
      containerType,
    });
  }

  public listCommentReactions(comments: ListCommentReactionOpts[]) {
    return this.client.listCommentReactions({
      baseUrl: this.getBaseUrl(),
      comments,
    });
  }

  public getCommentReactionDetails({
    containerId,
    commentId,
    emojiId,
  }: CommentReactionOpts) {
    return this.client.getCommentReactionDetails({
      baseUrl: this.getBaseUrl(),
      containerId,
      commentId,
      emojiId,
    });
  }

  public getViewedComments({ contentId, contentType }: ViewedCommentsOpts) {
    return this.client.getViewedComments({
      baseUrl: this.getBaseUrl(),
      contentId,
      contentType,
    });
  }

  public markCommentsAsViewed({ commentIds }: MarkCommentAsViewedOpts) {
    return this.client.markCommentsAsViewed({
      baseUrl: this.getBaseUrl(),
      commentIds,
    });
  }

  public getBoardTitle({ contentId }: BoardTitleOpts) {
    return this.client.getWhiteboardBoardTitle({
      baseUrl: this.getBaseUrl(),
      contentId,
    });
  }

  public getBoardTitleIsDefault({ contentId }: BoardTitleOpts) {
    return this.client.getWhiteboardBoardTitleIsDefault({
      baseUrl: this.getBaseUrl(),
      contentId,
    });
  }

  public toReferenceId(contentId: string) {
    return this.client.toReferenceId(contentId);
  }

  public toContentId(referenceId: string) {
    return this.client.toContentId(referenceId);
  }

  public createMentionNotification({ contentId, mentions }: MentionNotificationOpts) {
    return this.client.createMentionNotification({
      baseUrl: this.getBaseUrl(),
      contentId,
      mentions,
    });
  }

  public getContentLabels({ contentId, searchText }: LabelSearchOpts) {
    return this.client.getContentLabels({
      baseUrl: this.getBaseUrl(),
      contentId,
      searchText,
    });
  }

  public getContentPermissions({
    accountIds,
    contentId,
    cloudId,
    resourceType,
  }: ContentPermissionsOpts) {
    return this.client.getContentPermissions({
      baseUrl: this.getBaseUrl(),
      accountIds,
      contentId,
      cloudId,
      resourceType,
    });
  }

  public createContent(input: CreateContentMutationInput) {
    return this.client.createContent(this.getBaseUrl(), input);
  }

  public updateContentTitle(contentId: string, title: string, abortSignal?: AbortSignal) {
    return this.client.updateContentTitle(
      this.getBaseUrl(),
      contentId,
      title,
      abortSignal
    );
  }

  public getCanvasSessionBulkTokens({ contentIds }: CanvasSessionBulkTokensOpts) {
    return this.client.getCanvasSessionBulkTokens({
      baseUrl: this.getBaseUrl(),
      contentIds,
    });
  }
}

let confluenceService: ConfluenceService;

export const getConfluenceService = () => {
  if (!confluenceService) {
    confluenceService = new ConfluenceService(new ConfluenceClient());
  }

  return confluenceService;
};
